/* ==========================================================================
   AURA — CART CONTROLLER
   --------------------------------------------------------------------------
   PRINCIPLE: the UI must never know the endpoint. The stepper and remove
   links emit INTENT only (a bubbling `aura:quantity-change` event, a click);
   the controller translates intent into a transport call.

   Every mutation re-requests the whole `aura-cart` section through
   Shopify's real cart AJAX API (Section Rendering API: ?sections=) and the
   controller swaps #AuraCart's content with the fresh server render.
   Pricing, discounts, tax copy, the shipping bar and the empty/non-empty
   transition are computed once, in Liquid — never duplicated here.
   ========================================================================== */

/* -------- Transport (single implementation; cart has no reload fallback) - */

class AuraCartTransport {
  constructor() {
    this.changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    this.addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    this.updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    this.cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  }

  async post(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    return { cart: data, sections: data.sections || {} };
  }

  change(key, quantity) {
    return this.post(this.changeUrl, {
      id: key,
      quantity,
      sections: ['aura-cart', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });
  }

  remove(key) {
    return this.change(key, 0);
  }

  add(variantId, quantity) {
    return this.post(this.addUrl, {
      id: variantId,
      quantity: quantity || 1,
      sections: ['aura-cart', 'cart-icon-bubble'],
      sections_url: window.location.pathname,
    });
  }

  updateNote(note) {
    return this.post(this.updateUrl, { note });
  }

  /* Re-sync from the server without mutating — used after a cartUpdate event
     the transport didn't itself trigger (e.g. Frequently Bought Together). */
  async refresh() {
    const res = await fetch(`${this.cartUrl}?sections=aura-cart,cart-icon-bubble`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    const sections = await res.json();
    return { cart: null, sections };
  }
}

/* -------- Controller ----------------------------------------------------- */

class AuraCartController {
  constructor(root) {
    this.root = root;
    this.transport = new AuraCartTransport();
    this.pendingChanges = new Map(); // line key -> debounced applyChange

    this.bind();
    this.scheduleRecs();
  }

  bind() {
    this.root.addEventListener('aura:quantity-change', (e) => {
      const item = e.target.closest('[data-cart-item]');
      if (!item) return;
      this.queueChange(item.dataset.key, e.detail.value);
    });

    this.root.addEventListener('click', (e) => {
      const removeLink = e.target.closest('[data-remove]');
      if (removeLink) {
        e.preventDefault();
        const item = removeLink.closest('[data-cart-item]');
        if (item) this.applyRemove(item.dataset.key);
        return;
      }

      const addBtn = e.target.closest('[data-rec-add]');
      if (addBtn) {
        e.preventDefault();
        this.applyRecAdd(addBtn);
      }
    });

    this.root.addEventListener(
      'input',
      (e) => {
        if (!e.target.matches('[data-cart-note]')) return;
        if (!this.debouncedSaveNote) {
          this.debouncedSaveNote = debounce((value) => this.transport.updateNote(value), 500);
        }
        this.debouncedSaveNote(e.target.value);
      },
      true
    );

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source === 'aura-cart') return;
        this.transport.refresh().then(({ sections }) => this.swap(sections));
      });
    }
  }

  /* Debounce per line so rapid changes on one item don't drop a change made
     on another item within the same window. */
  queueChange(key, value) {
    if (!this.pendingChanges.has(key)) {
      this.pendingChanges.set(key, debounce((k, v) => this.applyChange(k, v), 350));
    }
    this.pendingChanges.get(key)(key, value);
  }

  async applyChange(key, quantity) {
    this.setBusy(true);
    try {
      const { sections } = await this.transport.change(key, quantity);
      this.swap(sections);
    } finally {
      this.setBusy(false);
    }
  }

  async applyRemove(key) {
    this.pendingChanges.delete(key);
    this.setBusy(true);
    try {
      const { sections } = await this.transport.remove(key);
      this.swap(sections);
    } finally {
      this.setBusy(false);
    }
  }

  async applyRecAdd(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.getAttribute('aria-disabled') === 'true') return;
    btn.setAttribute('aria-disabled', 'true');
    try {
      const { sections } = await this.transport.add(variantId, 1);
      this.swap(sections);
    } finally {
      btn.setAttribute('aria-disabled', 'false');
    }
  }

  setBusy(on) {
    this.root.classList.toggle('is-updating', on);
  }

  swap(sections) {
    if (!sections) return;

    if (sections['aura-cart']) {
      /* Preserve an in-progress, not-yet-saved note across a swap triggered
         by an unrelated action (e.g. a quantity change elsewhere) — the
         server's `cart.note` may not reflect the debounced save yet. */
      const noteEl = this.root.querySelector('[data-cart-note]');
      const pendingNote = noteEl && document.activeElement === noteEl ? noteEl.value : null;

      const doc = new DOMParser().parseFromString(sections['aura-cart'], 'text/html');
      const incoming = doc.getElementById('AuraCart');
      if (incoming) this.root.innerHTML = incoming.innerHTML;

      if (pendingNote != null) {
        const freshNote = this.root.querySelector('[data-cart-note]');
        if (freshNote) freshNote.value = pendingNote;
      }
    }

    if (sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const incoming = doc.getElementById('shopify-section-cart-icon-bubble') || doc.body.firstElementChild;
      const current = document.getElementById('cart-icon-bubble');
      if (incoming && current) current.innerHTML = incoming.innerHTML;
    }

    this.afterUpdate();
    this.announce();

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'aura-cart' });
    }
  }

  /* Screen readers only reliably re-announce a live region when its text
     content actually changes — clearing first guarantees a real mutation
     even when two consecutive updates would otherwise say the same thing. */
  announce() {
    const status = document.getElementById('AuraCartStatus');
    if (!status) return;
    status.textContent = '';
    window.setTimeout(() => {
      status.textContent = 'Košarica posodobljena';
    }, 60);
  }

  afterUpdate() {
    if (window.AuraQuantityStepper) window.AuraQuantityStepper.init();
    if (window.AuraAccordion) window.AuraAccordion.init();
    this.scheduleRecs();
  }

  /* The cart itself must paint instantly — recommendations are secondary,
     so they load once the browser is idle (or after a short timeout on
     browsers without requestIdleCallback, e.g. Safari). */
  scheduleRecs() {
    const run = () => this.loadRecs();
    if ('requestIdleCallback' in window) {
      requestIdleCallback(run, { timeout: 800 });
    } else {
      setTimeout(run, 400);
    }
  }

  async loadRecs() {
    const host = this.root.querySelector('.aura-cart');
    const wrap = this.root.querySelector('[data-recs]');
    const list = this.root.querySelector('[data-recs-list]');
    if (!host || !wrap || !list) return;

    const recUrl = host.dataset.recommendationsUrl;
    const productId = host.dataset.firstProductId;
    if (!recUrl || !productId) {
      wrap.setAttribute('hidden', '');
      return;
    }

    try {
      const url = `${recUrl}?section_id=aura-cart-recommendations&product_id=${productId}&intent=related&limit=4`;
      const text = await fetch(url).then((r) => r.text());
      const inner = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-aura-recs]');
      if (inner && inner.innerHTML.trim().length) {
        list.innerHTML = inner.innerHTML;
        wrap.removeAttribute('hidden');
      } else {
        wrap.setAttribute('hidden', '');
      }
    } catch (e) {
      /* optional */
    }
  }
}

/* -------- Boot ------------------------------------------------------------ */

document.addEventListener('DOMContentLoaded', () => {
  const root = document.getElementById('AuraCart');
  if (root) new AuraCartController(root);
});
